"use client";

import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import {
	Skeleton,
	type Props as SkeletonProps,
} from "@commerce-frontend/ui/base/Skeleton/Skeleton";
import type { TranslationValues, useTranslations } from "next-intl";

export type TranslationState = "none" | "loading" | "translated";

type Props = {
	t: ReturnType<typeof useTranslations>;
	tFallback: ReturnType<typeof useTranslations>;
	label: string;
	loadingLabel?: string;
	loadingIcon?: boolean;
	values?: TranslationValues;
	translation: TranslationState;
	skeletonProps?: SkeletonProps;
	reversed?: boolean; // text is shown translated first, untranslated after
};

export function TranslatableLabel({
	t,
	tFallback,
	label,
	values,
	loadingLabel,
	loadingIcon,
	translation,
	skeletonProps = { type: "text", textLines: 1 },
	reversed,
}: Props) {
	if (translation === "loading") {
		if (loadingLabel) {
			return t(loadingLabel, values);
		}
		if (loadingIcon) {
			return (
				<div className="flex items-center h-6">
					<Icon icon="loading-01" className="size-5 bg-gray-400" />
				</div>
			);
		}
		return (
			<div className="flex flex-col w-full">
				<Skeleton type="text" textLines={1} {...skeletonProps} />
			</div>
		);
	}

	const translatedText = t(label, values);
	const untranslatedText = tFallback(label, values);

	if (reversed) {
		return translation === "none" ? translatedText : untranslatedText;
	}

	return translation === "none" ? untranslatedText : translatedText;
}
